import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';

import {
  PesvViasHallazgosService,
  PesvViasHallazgos,
  PesvViasHallazgosPayload,
} from '../../../../../shared/services/pesv-vias-hallazgos.service';
import { PesvViasInspeccionesService } from '../../../../../shared/services/pesv-vias-inspecciones.service';
import { PesvViasZonasService } from '../../../../../shared/services/pesv-vias-zonas.service';
import { PesvViasRiesgosService } from '../../../../../shared/services/pesv-vias-riesgos.service';
import { ProbabilidadService } from '../../../../../shared/services/probabilidad.service';
import { Utils } from '../../../../../shared/utils/Utils';
import { TipoHallazgosService } from '../../../../../shared/services/tipo-hallazgos.service';

interface OpcionCatalogo {
  id: number;
  descripcion: string;
  clase?: string;
}

@Component({
  selector: 'app-vias-hallazgos',
  templateUrl: './vias-hallazgos.component.html',
  styleUrls: ['./vias-hallazgos.component.scss'],
})
export class ViasHallazgosComponent implements OnInit {
  form!: FormGroup;
  guardando = false;
  idHallazgo: number | null = null;
  activeTab = 1;
  cargandoListado = false;
  editando = false;
  hallazgos: PesvViasHallazgos[] = [];
  hallazgosFiltrados: PesvViasHallazgos[] = [];
  filtro = '';
  page = 1;
  pageSize = 10;
  readonly Number = Number;

  // Inspección sobre la que se registran los hallazgos (llega por la ruta :id)
  inspeccionId!: number;
  inspeccionActual: any = null;

  // Zona de la inspección
  zonaId: number | null = null;
  zonaActual: any = null;

  // Catálogos
  riesgosDeZona: any[] = [];
  tiposHallazgo: any[] = [];


  // Escala de probabilidad/consecuencia: se carga desde ProbabilidadService, respetando el id y orden de la BD
  escalaNivel: OpcionCatalogo[] = [];

  readonly nivelesRiesgo: OpcionCatalogo[] = [
    { id: 1, descripcion: 'BAJO', clase: 'bg-success' },
    { id: 2, descripcion: 'MEDIO', clase: 'bg-warning text-dark' },
    { id: 3, descripcion: 'ALTO', clase: 'bg-orange text-white' },
    { id: 4, descripcion: 'CRÍTICO', clase: 'bg-danger' },
  ];

  readonly prioridades: OpcionCatalogo[] = [
    { id: 1, descripcion: 'BAJA', clase: 'bg-success' },
    { id: 2, descripcion: 'MEDIA', clase: 'bg-warning text-dark' },
    { id: 3, descripcion: 'ALTA', clase: 'bg-orange text-white' },
    { id: 4, descripcion: 'CRÍTICA', clase: 'bg-danger' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private location: Location,
    private hallazgosService: PesvViasHallazgosService,
    private inspeccionesService: PesvViasInspeccionesService,
    private zonasService: PesvViasZonasService,
    private riesgosService: PesvViasRiesgosService,
    private probabilidadService: ProbabilidadService,
    private tipoHallazgosService: TipoHallazgosService
  ) {}

  ngOnInit(): void {
    this.inspeccionId = Number(this.route.snapshot.paramMap.get('id'));
    this.inicializarForm();
    this.getEscalaNivel();
    this.cargarInspeccion();
    this.cargarTiposHallazgo();
  }

  // Probabilidad y consecuencia comparten la misma escala, tal como llega de la BD
  getEscalaNivel(): void {
    this.probabilidadService.getAll().subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.escalaNivel = Array.isArray(lista) ? lista : [];
      },
      error: () => this.toastError('No se pudo cargar la escala de probabilidad/consecuencia.'),
    });
  }

  // ===== Contexto (inspección → zona → riesgos) =====

  // Carga la inspección para heredar vía, zona y riesgo, y luego dispara el resto de cargas
  cargarInspeccion(): void {
    this.inspeccionesService.getById(this.inspeccionId).subscribe({
      next: (respuesta: any) => {
        this.inspeccionActual = respuesta?.data ?? respuesta;
        this.zonaId = this.idCatalogo(this.inspeccionActual?.zona_id);

        this.form.patchValue({
          via_id: this.idCatalogo(this.inspeccionActual?.via_id),
          zona_id: this.zonaId,
          inspeccion_id: this.inspeccionId,
          riesgo_id: this.idCatalogo(this.inspeccionActual?.riesgo_id),
        });

        if (this.zonaId) {
          this.getZonaActual();
          this.getRiesgosDeZona();
          this.cargarHallazgos();
        }
      },
      error: () => this.toastError('No se pudo cargar la información de la inspección.'),
    });
  }

  getZonaActual(): void {
    this.zonasService.getById(this.zonaId as number).subscribe({
      next: (zona: any) => {
        this.zonaActual = zona?.data ?? zona;
        if (!this.form.get('via_id')?.value) {
          this.form.patchValue({ via_id: this.idCatalogo(this.zonaActual?.via_id) });
        }
      },
      error: () => this.toastError('No se pudo cargar la información de la zona.'),
    });
  }

  // Riesgos de la zona, para asociarlos opcionalmente al hallazgo
  getRiesgosDeZona(): void {
    this.riesgosService.getByIdZonas(this.zonaId as number).subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.riesgosDeZona = Array.isArray(lista) ? lista : [];
      },
      error: () => this.toastError('No se pudieron cargar los riesgos de la zona.'),
    });

  }

  cargarTiposHallazgo(): void {
    this.tipoHallazgosService.getAll().subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.tiposHallazgo = Array.isArray(lista) ? lista : [];
      },
      error: () => this.toastError('No se pudieron cargar los tipos de hallazgo.'),
    });
  }

  volver(): void {
    this.location.back();
  }

  // ===== Helpers de catálogos =====

  nombreTipo(id: any): string {
    return this.tiposHallazgo.find((item) => String(item.id) === String(id))?.descripcion ?? '';
  }

  nombreNivel(id: any): string {
    return this.nivelesRiesgo.find((item) => String(item.id) === String(id))?.descripcion ?? '';
  }

  nombrePrioridad(id: any): string {
    return this.prioridades.find((item) => String(item.id) === String(id))?.descripcion ?? '';
  }

  clasePrioridad(id: any): string {
    return this.prioridades.find((item) => String(item.id) === String(id))?.clase ?? 'bg-secondary';
  }

  // Devuelve el id como number (o null) aunque el backend lo envíe como
  // string ("3") o como objeto anidado ({ id: 3, ... }).
  private idCatalogo(valor: any): number | null {
    const id = valor != null && typeof valor === 'object'
      ? (valor.id ?? valor.codigo)
      : valor;
    if (id === null || id === undefined || id === '') return null;
    const numero = Number(id);
    return Number.isFinite(numero) ? numero : null;
  }

  // ===== Listado =====

  cargarHallazgos(): void {
    if (this.cargandoListado || !this.zonaId) return;

    this.cargandoListado = true;
    this.hallazgosService.getHallazgosZona(this.zonaId).subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.hallazgos = (Array.isArray(lista) ? lista : []).filter(
          (hallazgo: any) => String(hallazgo.inspeccion_id) === String(this.inspeccionId),
        );
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.hallazgos = [];
        this.hallazgosFiltrados = [];
        this.cargandoListado = false;
        this.toastError('No se pudieron cargar los hallazgos registrados.');
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.trim().toLowerCase();
    this.hallazgosFiltrados = this.hallazgos.filter((hallazgo) => {
      if (!termino) return true;
      return [
        hallazgo.codigo,
        this.nombreTipo(hallazgo.tipo_hallazgo),
        hallazgo.descripcion,
        hallazgo.causa,
        hallazgo.observaciones,
      ].some((valor) => String(valor ?? '').toLowerCase().includes(termino));
    });
    this.page = 1;
  }

  mostrarListado(): void {
    if (this.activeTab === 2) return;
    this.activeTab = 2;
  }

  // ===== Edición =====

  editarHallazgo(hallazgo: PesvViasHallazgos): void {
    if (!hallazgo?.id) return;
    this.hallazgosService.getById(hallazgo.id).subscribe({
      next: (respuesta: any) => {
        const datos = respuesta?.data ?? respuesta;
        this.idHallazgo = Number(datos.id ?? hallazgo.id);
        this.editando = true;

        this.form.patchValue({
          via_id: this.idCatalogo(datos.via_id) ?? this.idCatalogo(this.zonaActual?.via_id),
          zona_id: this.zonaId,
          inspeccion_id: this.inspeccionId,
          riesgo_id: this.idCatalogo(datos.riesgo_id),
          codigo: datos.codigo,
          tipo_hallazgo: this.idCatalogo(datos.tipo_hallazgo),
          descripcion: datos.descripcion,
          condicion_encontrada: datos.condicion_encontrada,
          causa: datos.causa,
          consecuencia: datos.consecuencia,
          probabilidad: this.idCatalogo(datos.probabilidad),
          consecuencia_nivel: this.idCatalogo(datos.consecuencia_nivel),
          nivel_riesgo: this.idCatalogo(datos.nivel_riesgo),
          prioridad: this.idCatalogo(datos.prioridad),
          requiere_accion: datos.requiere_accion === true || Number(datos.requiere_accion) === 1,
          fecha_deteccion: this.normalizarFecha(datos.fecha_deteccion),
          fecha_compromiso: this.normalizarFecha(datos.fecha_compromiso),
          observaciones: datos.observaciones,
          estado: datos.estado ?? 1,
        });
        this.form.get('prioridad')?.markAsDirty();
        this.activeTab = 1;
      },
      error: () => this.toastError('No se pudo cargar el hallazgo para editar.'),
    });
  }

  nuevoHallazgo(): void {
    this.idHallazgo = null;
    this.editando = false;
    this.form.reset(this.valoresIniciales());
    this.activeTab = 1;
  }

  cambiarEstado(hallazgo: PesvViasHallazgos): void {
    if (!hallazgo.id) return;
    const activar = Number(hallazgo.estado) !== 1;

    Swal.fire({
      icon: 'warning',
      title: `${activar ? 'Activar' : 'Inactivar'} hallazgo`,
      text: `¿Está seguro de ${activar ? 'activar' : 'inactivar'} este hallazgo?`,
      showCancelButton: true,
      confirmButtonText: `Sí, ${activar ? 'activar' : 'inactivar'}`,
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      const accion$ = activar
        ? this.hallazgosService.activar(hallazgo.id as number)
        : this.hallazgosService.inactivar(hallazgo.id as number);
      accion$.subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: activar ? 'Hallazgo activado' : 'Hallazgo inactivado',
            text: `El hallazgo fue ${activar ? 'activado' : 'inactivado'} correctamente.`,
            timer: 1600,
            showConfirmButton: false,
          });
          this.cargarHallazgos();
        },
        error: () => this.toastError(`No se pudo ${activar ? 'activar' : 'inactivar'} el hallazgo.`),
      });
    });
  }

  // ===== Fechas =====

  private hoy(): string {
    const fecha = new Date();
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  private normalizarFecha(fecha: any): string | null {
    if (!fecha) return null;
    const valor = String(fecha);
    if (/^\d{4}-\d{2}-\d{2}/.test(valor)) return valor.slice(0, 10);
    const convertida = new Date(fecha);
    if (Number.isNaN(convertida.getTime())) return null;
    const mes = String(convertida.getMonth() + 1).padStart(2, '0');
    const dia = String(convertida.getDate()).padStart(2, '0');
    return `${convertida.getFullYear()}-${mes}-${dia}`;
  }

  formatearFecha(fecha: any): string {
    const valor = this.normalizarFecha(fecha);
    if (!valor) return '';
    const [anio, mes, dia] = valor.split('-');
    return `${dia}/${mes}/${anio}`;
  }

  // ===== Formulario =====

  private valoresIniciales(): any {
    return {
      estado: 1,
      requiere_accion: false,
      fecha_deteccion: this.hoy(),
      via_id: this.idCatalogo(this.inspeccionActual?.via_id) ?? this.idCatalogo(this.zonaActual?.via_id),
      zona_id: this.zonaId,
      inspeccion_id: this.inspeccionId,
      riesgo_id: this.idCatalogo(this.inspeccionActual?.riesgo_id),
    };
  }

  inicializarForm(): void {
    this.form = this.fb.group({
      via_id: [null, Validators.required],
      zona_id: [null, Validators.required],
      inspeccion_id: [this.inspeccionId, Validators.required],
      riesgo_id: [null],
      codigo: [null, Validators.required],
      tipo_hallazgo: [null, Validators.required],
      descripcion: [null, Validators.required],
      condicion_encontrada: [null],
      causa: [null],
      consecuencia: [null],
      probabilidad: [null],
      consecuencia_nivel: [null],
      nivel_riesgo: [null],
      prioridad: [null],
      requiere_accion: [false],
      fecha_deteccion: [this.hoy(), Validators.required],
      fecha_compromiso: [null],
      observaciones: [null],
      estado: [1],
    });

    // Nivel de riesgo = probabilidad × consecuencia
    this.form.get('probabilidad')?.valueChanges.subscribe(() => this.recalcularNivelRiesgo());
    this.form.get('consecuencia_nivel')?.valueChanges.subscribe(() => this.recalcularNivelRiesgo());

    // La fecha compromiso solo es obligatoria si el hallazgo requiere acción
    this.form.get('requiere_accion')?.valueChanges.subscribe((requiere: boolean) => {
      const control = this.form.get('fecha_compromiso');
      if (requiere) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
        control?.setValue(null, { emitEvent: false });
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  // Matriz 5x5: 1-4 BAJO, 5-9 MEDIO, 10-16 ALTO, 17-25 CRÍTICO
  private recalcularNivelRiesgo(): void {
    const probabilidad = this.idCatalogo(this.form.get('probabilidad')?.value);
    const consecuencia = this.idCatalogo(this.form.get('consecuencia_nivel')?.value);

    if (!probabilidad || !consecuencia) {
      this.form.patchValue({ nivel_riesgo: null }, { emitEvent: false });
      return;
    }

    const puntaje = probabilidad * consecuencia;
    const nivel = puntaje <= 4 ? 1 : puntaje <= 9 ? 2 : puntaje <= 16 ? 3 : 4;
    this.form.patchValue({ nivel_riesgo: nivel }, { emitEvent: false });

    // Sugiere la prioridad igual al nivel mientras el usuario no la haya tocado
    const prioridad = this.form.get('prioridad');
    if (prioridad?.pristine) {
      prioridad.setValue(nivel, { emitEvent: false });
    }
  }

  get puntajeRiesgo(): number | null {
    const probabilidad = this.idCatalogo(this.form?.get('probabilidad')?.value);
    const consecuencia = this.idCatalogo(this.form?.get('consecuencia_nivel')?.value);
    return probabilidad && consecuencia ? probabilidad * consecuencia : null;
  }

  // ===== Guardado =====

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const camposInvalidos = Object.keys(this.form.controls)
        .filter((campo) => this.form.get(campo)?.invalid)
        .join(', ');
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: `Revisa los siguientes campos: ${camposInvalidos}.`,
      });
      return;
    }

    const raw = this.form.getRawValue();
    const requiereAccion = !!raw.requiere_accion;

    if (requiereAccion && raw.fecha_compromiso && raw.fecha_compromiso < raw.fecha_deteccion) {
      Swal.fire({
        icon: 'warning',
        title: 'Fechas inconsistentes',
        text: 'La fecha de compromiso no puede ser anterior a la fecha de detección.',
      });
      return;
    }

    const payload: PesvViasHallazgosPayload = {
      via_id: Number(raw.via_id),
      zona_id: this.idCatalogo(raw.zona_id),
      riesgo_id: this.idCatalogo(raw.riesgo_id),
      inspeccion_id: this.inspeccionId,
      codigo: String(raw.codigo).trim(),
      tipo_hallazgo: Number(raw.tipo_hallazgo),
      descripcion: String(raw.descripcion).trim(),
      condicion_encontrada: raw.condicion_encontrada?.trim() || null,
      causa: raw.causa?.trim() || null,
      consecuencia: raw.consecuencia?.trim() || null,
      nivel_riesgo: this.idCatalogo(raw.nivel_riesgo),
      probabilidad: this.idCatalogo(raw.probabilidad),
      consecuencia_nivel: this.idCatalogo(raw.consecuencia_nivel),
      prioridad: this.idCatalogo(raw.prioridad),
      requiere_accion: requiereAccion,
      fecha_deteccion: raw.fecha_deteccion,
      fecha_compromiso: requiereAccion ? raw.fecha_compromiso : null,
      estado: raw.estado ?? 1,
      observaciones: raw.observaciones?.trim() || null,
    };

    this.guardando = true;

    const peticion$ = this.idHallazgo
      ? this.hallazgosService.update(this.idHallazgo, payload)
      : this.hallazgosService.create(payload);

    peticion$.subscribe({
      next: () => this.finalizarGuardado(),
      error: (err) => {
        this.guardando = false;
        this.toastError(err?.error?.message ?? err?.error?.error ?? 'Ocurrió un error al guardar el hallazgo.');
      },
    });
  }

  private finalizarGuardado(): void {
    this.guardando = false;
    Swal.fire({
      icon: 'success',
      title: this.idHallazgo ? 'Hallazgo actualizado' : 'Hallazgo registrado',
      text: 'La información se guardó correctamente.',
      timer: 1800,
      showConfirmButton: false,
    });

    this.cargarHallazgos();
    this.editando = false;
    this.idHallazgo = null;
    this.form.reset(this.valoresIniciales());
    this.activeTab = 2;
  }

  cancelar(): void {
    this.nuevoHallazgo();
    this.activeTab = 2;
    this.cargarHallazgos();
  }

  private toastError(mensaje: string): void {
    Swal.fire({ icon: 'error', title: 'Error', text: mensaje });
  }

  exportarExcel(): void {
    Utils.exportVisibleTableToExcel(
      'tblViasHallazgos',
      'listado_vias_hallazgos',
      'Hallazgos de vías',
    );
  }

  exportarPdf(): void {
    Utils.exportToPDF(
      'tblViasHallazgos',
      'listado_vias_hallazgos',
      'Listado de hallazgos de vías',
      true,
    );
  }
}